import { brandSchema, marketSchema } from 'data/schema';
import { getNewSessionData, userSession } from 'state/session';
import { setStorageItem } from 'utils/ui/storage';

/**
 * Coordinates a dealer switch end-to-end and resolves only after the new session state is applied.
 *
 * Flow (mirrors the deprecated `User.dealerId` setter, but awaitable):
 * 1. Validate — brand (last letter) and market (first three letters) are parsed from the dealer id;
 *    an invalid id throws/rejects BEFORE any write, leaving storage and session untouched.
 * 2. Persist — write `auth:dealerId` to sessionStorage. This is the backing store of the
 *    `User.dealerId` getter, so the user object is consistent from this point on. It must happen
 *    BEFORE `getNewSessionData()`, which reads `user.dealerId` into the fresh session.
 * 3. Reset — apply a fresh session with the new brand/market via `setValues({ reset: true })`.
 *    The reset applies and emits synchronously, so callers that await this function observe the
 *    fully switched session (a change in brand/market triggers re-routing in standalone).
 *
 * Dependency direction: this module statically imports `state/session` (which statically imports
 * `utils/auth/user`); `user.ts` may only reach this module via dynamic import to stay acyclic.
 */
export async function selectDealer(dealerId: string): Promise<void> {
  // Extract brand (last letter) and market (first three letters) from dealer ID
  const brand = brandSchema.parse(dealerId.slice(-1));
  const market = marketSchema.parse(dealerId.slice(0, 3));

  setStorageItem('sessionStorage', 'auth:dealerId', dealerId);

  userSession().setValues(
    {
      ...getNewSessionData(),
      brand,
      market,
    },
    { reset: true }
  );
}
